import { useRef, useState } from "react";
import { MessageSquarePlus, Loader2, ImagePlus, X } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";

const feedbackSchema = z.object({
  message: z
    .string()
    .trim()
    .min(3, "Please write at least a few words.")
    .max(2000, "Feedback must be under 2000 characters."),
});

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

interface FeedbackButtonProps {
  className?: string;
  iconOnlyOnMobile?: boolean;
}

export default function FeedbackButton({ className, iconOnlyOnMobile = true }: FeedbackButtonProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetScreenshot = () => {
    setScreenshotFile(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_SCREENSHOT_TYPES.includes(file.type)) {
      toast.error("Please choose a PNG, JPG, WEBP, or GIF image.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      toast.error("Screenshot must be under 5 MB.");
      e.target.value = "";
      return;
    }
    setScreenshotFile(file);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = feedbackSchema.safeParse({ message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid feedback");
      return;
    }
    setSubmitting(true);
    try {
      const recipientEmail = user?.email ?? "anonymous@oplif.com";
      let screenshotUrl: string | undefined;

      if (screenshotFile) {
        if (!user?.id) {
          toast.error("Please sign in to attach a screenshot.");
          setSubmitting(false);
          return;
        }
        const ext = screenshotFile.name.split(".").pop()?.toLowerCase() || "png";
        const safeExt = ext.replace(/[^a-z0-9]/g, "").slice(0, 5) || "png";
        const path = `${user.id}/${Date.now()}-${crypto.randomUUID()}.${safeExt}`;
        const { error: uploadErr } = await supabase.storage
          .from("feedback-screenshots")
          .upload(path, screenshotFile, {
            contentType: screenshotFile.type,
            upsert: false,
          });
        if (uploadErr) throw uploadErr;
        // Bucket is private — generate a long-lived signed URL the admin can
        // open from the feedback email.
        const { data: signed, error: signErr } = await supabase.storage
          .from("feedback-screenshots")
          .createSignedUrl(path, 60 * 60 * 24 * 30); // 30 days
        if (signErr) throw signErr;
        screenshotUrl = signed.signedUrl;
      }

      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "user-feedback",
          recipientEmail, // template has fixed `to`, this is just for the caller-match check
          idempotencyKey: `feedback-${user?.id ?? "anon"}-${Date.now()}`,
          templateData: {
            userEmail: user?.email ?? "Unknown",
            message: parsed.data.message,
            pagePath: typeof window !== "undefined" ? window.location.pathname : "/dashboard",
            timestamp: new Date().toISOString(),
            screenshotUrl,
          },
        },
      });
      if (error) throw error;
      toast.success("Thanks! Your feedback was sent.");
      setMessage("");
      resetScreenshot();
      setOpen(false);
    } catch (err) {
      console.error("feedback send failed", err);
      toast.error("Could not send feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetScreenshot();
      }}
    >
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={`gap-1.5 h-8 px-2 sm:px-3 ${className ?? ""}`}
        >
          <MessageSquarePlus className="w-4 h-4" />
          <span className={iconOnlyOnMobile ? "hidden sm:inline" : ""}>
            Send feedback
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what's working, what's broken, or what you wish Oplif did. Goes
            straight to the founder.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What's on your mind?"
            rows={6}
            maxLength={2000}
            disabled={submitting}
            autoFocus
            required
          />

          {/* Optional screenshot */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleFileChange}
              disabled={submitting}
            />
            {!screenshotPreview ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || !user?.id}
                className="gap-1.5"
              >
                <ImagePlus className="w-4 h-4" />
                {user?.id ? "Attach screenshot (optional)" : "Sign in to attach a screenshot"}
              </Button>
            ) : (
              <div className="relative inline-block">
                <img
                  src={screenshotPreview}
                  alt="Screenshot preview"
                  className="max-h-40 rounded-md border border-border"
                />
                <button
                  type="button"
                  onClick={resetScreenshot}
                  disabled={submitting}
                  aria-label="Remove screenshot"
                  className="absolute -top-2 -right-2 rounded-full bg-background border border-border shadow p-1 hover:bg-muted"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {user?.email ? (
              <>We'll include your email ({user.email}) so we can reply.</>
            ) : (
              <>Sign in so we can reply to your feedback.</>
            )}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || message.trim().length < 3}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                  {screenshotFile ? "Uploading…" : "Sending…"}
                </>
              ) : (
                "Send feedback"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
